import React, {useCallback, useContext, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    UserDetailsController,
} from 'src/modules/profile/account/create-user/steps/user-details/user-details.controller';
import {clearUserInformation} from 'src/modules/profile/user/services/user-profile-slice';
import {getUserShortInfo} from 'src/openapi/admin/apis/GetUserShortInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

type Props = {
    userId: number,
    accountId: number,
};

export const UserDetails = ({userId, accountId}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const navigate = useNavigate();

    const {setSuccessMessage} = useContext(MessageContext);
    const {canEditUser} = useAdminAccessRight();
    const {userFieldChange, userSubmit} = useEventTracking();

    const onProceed = useCallback((existedUserId?: number) => {
        if (existedUserId && typeof existedUserId === 'number') {
            navigate(`/profile/user/${existedUserId}`);
        } else {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
            dispatch(clearUserInformation());
            apiDispatch(getUserShortInfo({userId}));
            userSubmit({place: 'User Details', submit: true});
        }
    }, [userId, apiDispatch, dispatch, navigate, setSuccessMessage, userSubmit]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            parameterName,
            section,
            viewType: 'Existed',
        });
    };

    const editDisabled = useMemo(() => !canEditUser(accountId), [canEditUser, accountId]);

    return (
        <ValidationContextProvider>
            <UserDetailsController {...{userId: +userId, accountId, onProceed, editDisabled, onFieldChange}}>
                {({content, busy, onSave, onDiscard, isLoading, loadingError, isChunkChanged}) => (
                    <ServerResponseHandler serverError={loadingError} sectionName="User Details">
                        <ProfileMain header={(
                            <Item grow={1}>
                                <Group justify="space-between">
                                    <Typography
                                        fontWeight="semi-bold"
                                        variant="heading-level-3">
                                        User Details
                                    </Typography>
                                    {!editDisabled && (
                                        <Group>
                                            <Button
                                                variant="Type.SECONDARY"
                                                onClick={() => {
                                                    userSubmit({place: 'User Details', submit: false});
                                                    onDiscard();
                                                    setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
                                                }}
                                                disabled={!isChunkChanged || isLoading}>
                                                Discard Changes
                                            </Button>
                                            <SaveWithReasonButton
                                                busy={busy}
                                                disabled={!isChunkChanged || isLoading}
                                                onSave={onSave}
                                            />
                                        </Group>
                                    )}
                                </Group>
                            </Item>
                        )}>
                            {content}
                        </ProfileMain>
                    </ServerResponseHandler>
                )}
            </UserDetailsController>
        </ValidationContextProvider>
    );
};
